import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Router from "next/router";
import { Elements, useStripe } from "@stripe/react-stripe-js";

import GenericSpinner from "@kikoff/components/src/v1/animations/GenericSpinner";
import useErrorConsumer from "@kikoff/hooks/src/useErrorConsumer";
import { web } from "@kikoff/proto/src/protos";

import { Namespace } from "@component/flows/components/Namespace";
import PersistentAppBar from "@component/flows/components/PersistentAppBar";
import usePageFlow from "@component/flows/hooks/usePageFlow";
import PageFlow, { useRouterLocation } from "@component/flows/PageFlow";
import {
  FIRST_MONTH_DISCOUNT_PROMO,
  LAST_MONTH_FREE_PROMO,
} from "@constant/promos";
import { initCreditV2 } from "@feature/credit";
import { selectExperimentVariant } from "@feature/experiment";
import { selectCbsLoan } from "@feature/loans";
import { provideConsumable, selectFeatureFlag } from "@feature/page";
import {
  selectActiveSubscription,
  selectCheckoutPreview,
  selectCreditServiceProduct,
  updateOrders,
} from "@feature/shopping";
import { fetchUser } from "@feature/user";
import { useBackendExperiment } from "@src/experiments/context";
import { track } from "@util/analytics";
import { nativeDispatch } from "@util/mobile";

import AutopayDiscountView from "./_views/autopay_discount";
import CbsUpsellView from "./_views/cbs_upsell";
import CreditIncreaseCongratsView from "./_views/creditline_increase_congrats";
import HdyhauSurveyView from "./_views/hdyhau_survey/hdyhau_survey";
import InterstitialPage from "./_views/interstitial";
import NoOffersPage from "./_views/loan_painted_door/no_offers";
import OldStore, { addProductToBag } from "./_views/old_store";
import OpenCreditAccountFirstPayment from "./_views/open_credit_account_first_payment";
import OrderConfirmationView from "./_views/order_confirmation";
import PersonaFlowView from "./_views/persona_flow";
import PersonalizedOnboarding from "./_views/personalized_onboarding";
import PromoOffer from "./_views/promo_offer";
import ReconfirmIdentityPage from "./_views/reverify/reconfirm_identity";
import ReverificationPage from "./_views/reverify/reverification";
import ReverificationFailedPage from "./_views/reverify/reverification_failed";
import OrderReview from "./_views/review_order";
import SelectPlanPage from "./_views/select_plan";
import SelectProductView from "./_views/select_product";
import SetupRepaymentView from "./_views/setup_repayment";
import StreamlinedEducation from "./_views/streamlined_education";
import StreamlinedPayment from "./_views/streamlined_payment";

import styles from "./[[...storeRoute]].module.scss";

export const goals = ["build", "borrow"];
export type Goal = typeof goals[number];

export default function ChargeAccountOffer() {
  const dispatch = useDispatch();
  const stripe = useStripe();
  const error = useErrorConsumer();

  const { flowController, registerPage, registerLayout } = usePageFlow();
  const location = useRouterLocation("/dashboard/store");

  const planPageNoBack = useMemo(() => {
    const url = new URL(window.location.href);
    return url.searchParams.get("back") === "false";
  }, []);

  const credit = useSelector((state) => state.credit.credit);
  const order = useSelector((state) => state.shopping.checkoutResponse?.order);
  const emphasizeLending = useSelector((state) => state.page.emphasizeLending);
  const creditLine = useSelector((state) => state.creditLine.account);
  const [activeSubscription] = useSelector(selectActiveSubscription.load());

  const autopayEnabledWhenOpened = useMemo(() => creditLine?.autopayEnabled, [
    !!creditLine,
  ]);

  const { needsReverification } = useSelector((state) => state.user.proto);
  const personaUserActionRequired = useSelector(
    (state) =>
      state.user.proto?.personaAddressVerification?.addressVerificationState ===
      "pending_user_action"
  );

  const hdyhauSurvey = useSelector(
    selectFeatureFlag("hdyhau_onboarding_experiment")
  );

  const [todosV2Variant, loadingTodosV2Variant] = useSelector(
    selectExperimentVariant.load("engagementTodosV2")
  );

  const isStandaloneSubscription = activeSubscription?.planInfo.noCreditLine;

  useEffect(() => {
    const url = new URL(window.location.href);
    const isOrderConfirmationPage =
      url.pathname === "/dashboard/store/order-confirmation";
    const token = url.searchParams.get("order");
    if (isOrderConfirmationPage && token) {
      dispatch(updateOrders())
        .then((response) => {
          const foundOrder = response.orders.find((o) => o.token === token);
          if (foundOrder) {
            Router.replace(`/dashboard/store/order/${token}`);
          }
        })
        .catch((err: Error) => {
          error.throw(err.message);
        });
    }
  }, []);

  useEffect(() => {
    dispatch(fetchUser.persona());
    dispatch(fetchUser.features());
    dispatch(initCreditV2());
  }, []);

  // Don't allow store if reached max credit lines and the last one is closed
  const allowStore = (() => {
    if (!creditLine) return true;
    if (!creditLine.closedAt) return true;
    if (!creditLine.allowReactivation) return false;
    return true;
  })();

  useEffect(() => {
    if (!allowStore && !nativeDispatch("close")) Router.replace("/dashboard");
  }, []);

  useEffect(() => {
    if (isStandaloneSubscription && !nativeDispatch("close")) {
      Router.replace("/dashboard");
    }
  }, [isStandaloneSubscription]);

  // Enable CBS immediate upsell for users without any kind of payment tutorial
  const cbsImmediateUpsell = (() => {
    const cbsLoan = useSelector(selectCbsLoan({ includeClosed: true }));
    const paymentTutorialEnabled = (() => {
      const base = useSelector(selectFeatureFlag("ca_payment_tutorial"));
      const aggressive = useSelector(
        selectFeatureFlag("ca_aggressive_payment")
      );
      return base || aggressive;
    })();
    const immediateUpsellEnabled = useSelector(
      selectFeatureFlag("cbs_immediate_upsell_v2")
    );

    return !cbsLoan && !paymentTutorialEnabled && immediateUpsellEnabled;
  })();

  const [creditServiceProduct] = useSelector(
    selectCreditServiceProduct.load("BASIC")
  );
  const [preview] = useSelector(
    selectCheckoutPreview.load.after(creditServiceProduct)(
      creditServiceProduct?.token
    )
  );
  const hasPreselectedPlan = useMemo(() => !!preview, []);

  const displayCreditIncreaseCongrats =
    preview && preview.newCreditLimitCents > preview.creditLimitCents;
  const clIsClosed = !!creditLine?.closedAt;
  const activating = useMemo(() => !creditLine || clIsClosed, []);

  const blockDashboard = useSelector(
    selectFeatureFlag("block_dashboard_unless_ca")
  );

  const openCaNeedsFirstPayment = (() => {
    const historicallyTrue = useRef(false);

    const current =
      blockDashboard &&
      creditLine &&
      creditLine.autopayEnabled &&
      !creditLine.activatedAt;
    if (current) historicallyTrue.current = true;

    return current || historicallyTrue.current;
  })();

  const caPaymentSetUpAfterOpen = useSelector(
    (state) => state.shopping.caPaymentSetUpAfterOpen
  );

  const firstMonthDiscountPromo = useSelector((state) => {
    return state.user.proto?.promos?.find(
      (promo) => promo.name === FIRST_MONTH_DISCOUNT_PROMO
    );
  });

  const lastMonthFreePromo = useSelector((state) => {
    return state.user.proto?.promos?.find(
      (promo) => promo.name === LAST_MONTH_FREE_PROMO
    );
  });

  // This handles the edgecase that a user can schedule a payment for "today",
  // and successfully attach a card, but it fails for insufficient funds.
  const creditlinePaymentFailed =
    creditLine?.delayedPayment?.status ===
    web.public_.CreditLineAccount.DelayedPayment.Status.FAILED;

  const [selectedGoal, setSelectedGoal] = useState<Goal>(null);

  const showPersonalizedOnboarding =
    credit?.currentCreditScore.score >= 300 &&
    useBackendExperiment("personalizedOnboarding") !== "control";

  const autoPayDiscountEligible =
    useBackendExperiment("basicAutopayDiscount") === "discount";

  useEffect(() => {
    if (preview) {
      addProductToBag(dispatch, preview, creditServiceProduct);
    }
  }, [preview]);

  if (!allowStore) return null;

  if (isStandaloneSubscription) return null;

  if ((activating && !preview) || loadingTodosV2Variant)
    return <GenericSpinner center size={24} />;

  // NOTE: we're not showing the streamlined views when a user is returning to
  //   the store, and already has a Credit Line product.
  // NOTE: if the registered paths are the same, there is an edgecase where the
  //   persistent app bar from a different view will show up. Ensure you always
  //   call configFlow({}) even if no settings otherwise this issue will arise.
  return (
    <PageFlow
      className={styles["flat-onboarding-flow"]}
      flowController={flowController}
      location={location}
      onComplete={() => {
        nativeDispatch("requestReview");
        if (!nativeDispatch("close")) {
          if (todosV2Variant === "candidate") {
            return Router.replace("/dashboard/goals");
          }
          Router.replace("/dashboard");
        }
      }}
      // Amplitude event backwards compat
      eventRouteAliases={{
        "/": "make-order > select-product",
        "/review-order": "make-order > review-order",
        "/setup-repayment": "make-order > setup-repayment",
        "/agreement": "make-order > agreement",
        "/mobile-app": "mobile-adoption",
        "/identity-verification": "persona-flow",
        "/credit-increase": "credit-increase-congrats",
      }}
    >
      {activating ? (
        <Namespace {...registerLayout("/")}>
          {firstMonthDiscountPromo && (
            <PromoOffer
              promoName={FIRST_MONTH_DISCOUNT_PROMO}
              {...registerPage("/promo/first-month-half-off")}
            />
          )}
          {lastMonthFreePromo && (
            <PromoOffer
              promoName={LAST_MONTH_FREE_PROMO}
              {...registerPage("/promo/last-month-free")}
            />
          )}
          {showPersonalizedOnboarding && (
            <PersonalizedOnboarding
              {...registerPage("/personalized-onboarding")}
            />
          )}
          <StreamlinedEducation
            {...registerPage("/get-started")}
            showBack={showPersonalizedOnboarding} // only show back if personalized onboarding is shown
          />
          {!hasPreselectedPlan && (
            <SelectPlanPage
              {...registerPage("/select-plan")}
              showStoreDisclaimer
              selectedGoal={selectedGoal}
              onGoalChange={(goal) => {
                setSelectedGoal(goal);
              }}
              showMoneyBackGuarantee
              showBack={!planPageNoBack}
            />
          )}
          <NoOffersPage {...registerPage.detached("/no-offers")} />
          <OldStore {...registerPage.detached("/catalog")} />
          {emphasizeLending && <InterstitialPage {...registerPage("/info")} />}
          {needsReverification && (
            <Namespace {...registerLayout("/reverify")}>
              <ReconfirmIdentityPage {...registerPage("/")} />
              <ReverificationPage {...registerPage("/running")} />
            </Namespace>
          )}
          {preview && <StreamlinedPayment {...registerPage("/review-order")} />}
          {openCaNeedsFirstPayment && (
            <OpenCreditAccountFirstPayment
              {...registerPage("make-first-payment")}
            />
          )}
        </Namespace>
      ) : (
        <PersistentAppBar
          {...registerLayout("/")}
          defaultControls={!openCaNeedsFirstPayment}
          firstBack={
            window.history.length > 1 &&
            (() => {
              Router.back(); // TODO: use browser history after its implemented instead of this
              dispatch(provideConsumable("lastWelcome"));
            })
          }
          noBack={openCaNeedsFirstPayment}
          onClose={() => {
            track.tap("Store - Exit");
          }}
        >
          {openCaNeedsFirstPayment && (
            <OpenCreditAccountFirstPayment
              {...registerPage("make-first-payment")}
            />
          )}
          {!openCaNeedsFirstPayment && !caPaymentSetUpAfterOpen && (
            <SelectProductView {...registerPage("/")} />
          )}
          {preview && <OrderReview {...registerPage("review-order")} />}
        </PersistentAppBar>
      )}

      {displayCreditIncreaseCongrats && (
        <CreditIncreaseCongratsView
          {...registerPage("credit-increase")}
          preview={preview}
        />
      )}

      {(order || preview) &&
        creditLine &&
        !autopayEnabledWhenOpened &&
        autoPayDiscountEligible &&
        (order || preview?.order).orderItems[0].product.plan ===
          web.public_.SubscriptionPlan.BASIC && (
          <AutopayDiscountView
            {...registerPage("autopay-discount")}
            creditLineToken={creditLine.token}
          />
        )}

      {(order || preview || caPaymentSetUpAfterOpen) &&
        creditLine &&
        (creditLine.activatedAt ||
          creditLine.autopayEnabled ||
          (creditLine.delayedPayment && !creditlinePaymentFailed)) && (
          <OrderConfirmationView
            {...registerPage("order-confirmation")}
            activating={activating}
          />
        )}

      {hdyhauSurvey && (
        <>
          <HdyhauSurveyView
            {...registerPage("onboarding-survey")}
            step="first-question"
          />
          <HdyhauSurveyView
            {...registerPage("onboarding-survey/:keySuffix")}
            step="second-question"
          />
          <HdyhauSurveyView
            {...registerPage("onboarding-survey/:keySuffix/other")}
            step="freeform-question"
          />
          <HdyhauSurveyView
            {...registerPage("onboarding-survey/thank-you")}
            step="thank-you"
          />
        </>
      )}

      {!order && !preview && creditLine && (
        <Elements stripe={stripe}>
          <SetupRepaymentView
            {...registerPage("setup-repayment")}
            activating={activating}
          />
        </Elements>
      )}

      {personaUserActionRequired && (
        <PersonaFlowView {...registerPage("persona-flow")} />
      )}

      {cbsImmediateUpsell && <CbsUpsellView {...registerPage("cbs-upsell")} />}
      {needsReverification && (
        <ReverificationFailedPage {...registerPage("reverification-failed")} />
      )}
    </PageFlow>
  );
}
